import type { PostgrestError } from '@supabase/supabase-js';
import { AuthError } from '@supabase/supabase-js';
import type { ZodError } from 'zod';

export type ServiceErrorCode =
  | 'unauthorized'
  | 'forbidden'
  | 'validation_failed'
  | 'not_found'
  | 'conflict'
  | 'bad_request'
  | 'supabase_error'
  | 'unexpected';

export interface ServiceErrorInit {
  code: ServiceErrorCode;
  message: string;
  status?: number;
  cause?: unknown;
  details?: unknown;
  fieldErrors?: Record<string, string>;
}

export class ServiceError extends Error {
  readonly code: ServiceErrorCode;
  readonly status?: number;
  readonly details?: unknown;
  readonly fieldErrors?: Record<string, string>;

  constructor(init: ServiceErrorInit) {
    super(init.message);
    this.name = 'ServiceError';
    this.code = init.code;
    this.status = init.status;
    this.details = init.details;
    this.fieldErrors = init.fieldErrors;

    if (init.cause !== undefined) {
      (this as Error & { cause?: unknown }).cause = init.cause;
    }
  }
}

export function isServiceError(error: unknown): error is ServiceError {
  return error instanceof ServiceError;
}

export function createServiceError(init: ServiceErrorInit): ServiceError {
  return new ServiceError(init);
}

export function createUnauthorizedError(
  message = 'You are not authorized to perform this action.',
): ServiceError {
  return new ServiceError({ code: 'unauthorized', message, status: 401 });
}

export function createForbiddenError(
  message = 'You do not have permission to perform this action.',
): ServiceError {
  return new ServiceError({ code: 'forbidden', message, status: 403 });
}

export function createNotFoundError(
  message = 'The requested resource was not found.',
): ServiceError {
  return new ServiceError({ code: 'not_found', message, status: 404 });
}

export function createConflictError(
  message = 'The resource is in a conflicting state.',
): ServiceError {
  return new ServiceError({ code: 'conflict', message, status: 409 });
}

export function createValidationError(
  message: string,
  fieldErrors?: Record<string, string>,
  details?: unknown,
): ServiceError {
  return new ServiceError({
    code: 'validation_failed',
    message,
    status: 422,
    fieldErrors,
    details,
  });
}

export function fromZodError(error: ZodError, message = 'Validation failed'): ServiceError {
  const fieldErrors = Object.fromEntries(
    Object.entries(error.formErrors.fieldErrors).flatMap(([key, values]) =>
      Array.isArray(values) && values.length > 0 ? [[key, values[0]]] : [],
    ),
  );

  return createValidationError(message, fieldErrors, error.flatten());
}

export function fromPostgrestError(error: PostgrestError): ServiceError {
  let code: ServiceErrorCode = 'supabase_error';
  let status = error.code ? Number.parseInt(error.code, 10) : undefined;

  switch (error.code) {
    case '23505':
      code = 'conflict';
      status = 409;
      break;
    case '23503':
      code = 'bad_request';
      status = 400;
      break;
    case 'PGRST301':
      code = 'not_found';
      status = 404;
      break;
    default:
      break;
  }

  const details =
    typeof error.details === 'string' && error.details.length > 0 ? error.details : error.hint;

  return new ServiceError({
    code,
    message: error.message,
    status,
    details,
    cause: error,
  });
}

function isRecordWithCodeAndMessage(value: unknown): value is { code: unknown; message: unknown } {
  return typeof value === 'object' && value !== null && 'code' in value && 'message' in value;
}

function coerceErrorMessage(value: unknown, fallback: string): string {
  if (typeof value === 'string') {
    return value;
  }

  if (typeof value === 'number') {
    return value.toString();
  }

  if (value instanceof Error && typeof value.message === 'string') {
    return value.message;
  }

  return fallback;
}

export function normalizeError(error: unknown): ServiceError {
  if (isServiceError(error)) {
    return error;
  }

  if (error instanceof AuthError) {
    if (error.status === 401) {
      return createUnauthorizedError(error.message);
    }
    return createServiceError({
      code: 'unexpected',
      message: error.message,
      status: error.status,
      cause: error,
    });
  }

  if (isRecordWithCodeAndMessage(error)) {
    const message = coerceErrorMessage(error.message, 'Unexpected error');
    return createServiceError({
      code: 'unexpected',
      message,
      cause: error,
    });
  }

  return createServiceError({ code: 'unexpected', message: 'Unexpected error', cause: error });
}
